import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, Users, AlertTriangle, CheckCircle, Clock, 
  Plus, Trash2, Play, Target, TrendingUp, UserCheck, Mail,
  RefreshCw, ChevronDown, ChevronRight, X, Search,
  BarChart3, PieChart, AlertCircle, Shield, BookOpen
} from 'lucide-react';
import { trainingAPI } from '../lib/api';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

const CAMPAIGN_TYPES = [
  { value: 'general', label: 'General Security', icon: Shield },
  { value: 'phishing', label: 'Phishing Awareness', icon: Mail },
  { value: 'compliance', label: 'Compliance Training', icon: BookOpen },
  { value: 'security_basics', label: 'Security Basics', icon: Target },
  { value: 'incident_response', label: 'Incident Response', icon: AlertTriangle }
];

const STATUS_COLORS = {
  draft: 'bg-slate-500',
  active: 'bg-green-500',
  completed: 'bg-blue-500',
  archived: 'bg-gray-500'
};

const RISK_COLORS = ['#22c55e', '#eab308', '#ef4444'];

const TrainingDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [phishingSimulations, setPhishingSimulations] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  // Modules state
  const [modules, setModules] = useState([]);
  const [moduleCategories, setModuleCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [myProgress, setMyProgress] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showModuleDetail, setShowModuleDetail] = useState(false);
  const [moduleLoading, setModuleLoading] = useState(false);
  
  // Modal states
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Form states
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    type: 'general',
    dueDate: '',
    passingScore: 80
  });
  const [employeeForm, setEmployeeForm] = useState({
    email: '',
    name: '',
    department: '',
    jobTitle: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, campaignsRes, employeesRes, phishingRes, modulesRes, categoriesRes, progressRes] = await Promise.all([
        trainingAPI.getStats(),
        trainingAPI.getCampaigns(),
        trainingAPI.getEmployees(),
        trainingAPI.getPhishingSimulations(),
        trainingAPI.getModules(),
        trainingAPI.getModuleCategories(),
        trainingAPI.getMyProgress()
      ]);
      setStats(statsRes.data);
      setCampaigns(campaignsRes.data);
      setEmployees(employeesRes.data);
      setPhishingSimulations(phishingRes.data);
      setModules(modulesRes.data);
      setModuleCategories(categoriesRes.data);
      setMyProgress(progressRes.data);
    } catch (error) {
      console.error('Error loading training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async (e) => {
    e.preventDefault();
    try {
      await trainingAPI.createCampaign(campaignForm);
      setShowCreateCampaign(false);
      setCampaignForm({ name: '', description: '', type: 'general', dueDate: '', passingScore: 80 });
      loadData();
    } catch (error) {
      console.error('Error creating campaign:', error);
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      await trainingAPI.createEmployee(employeeForm);
      setShowAddEmployee(false);
      setEmployeeForm({ email: '', name: '', department: '', jobTitle: '' });
      loadData();
    } catch (error) {
      console.error('Error adding employee:', error);
    }
  };

  const handleActivateCampaign = async (campaignId) => {
    try {
      await trainingAPI.updateCampaignStatus(campaignId, { status: 'active' });
      loadData();
    } catch (error) {
      console.error('Error activating campaign:', error);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign and all assignments?')) return;
    try {
      await trainingAPI.deleteCampaign(campaignId);
      loadData();
    } catch (error) {
      console.error('Error deleting campaign:', error);
    }
  };

  const handleAssignEmployees = async () => {
    if (!selectedCampaign || selectedEmployees.length === 0) return;
    try {
      await trainingAPI.assignEmployees(selectedCampaign.id, {
        employees: selectedEmployees.map(e => ({
          email: e.email,
          name: e.name,
          department: e.department
        }))
      });
      setShowAssignModal(false);
      setSelectedCampaign(null);
      setSelectedEmployees([]);
      loadData();
    } catch (error) {
      console.error('Error assigning employees:', error);
    }
  };

  const openAssignModal = (campaign) => {
    setSelectedCampaign(campaign);
    setSelectedEmployees([]);
    setShowAssignModal(true);
  };

  const filteredEmployees = employees.filter(e => 
    e.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRiskLabel = (score) => {
    if (score >= 70) return { label: 'High Risk', color: 'text-red-400' };
    if (score >= 40) return { label: 'Medium Risk', color: 'text-yellow-400' };
    return { label: 'Low Risk', color: 'text-green-400' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  const riskDistributionData = stats ? [
    { name: 'Low', value: stats.riskDistribution?.low || 0 },
    { name: 'Medium', value: stats.riskDistribution?.medium || 0 },
    { name: 'High', value: stats.riskDistribution?.high || 0 }
  ] : [];

  return (
    <div data-testid="training-dashboard" className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              Security Awareness Training
            </h1>
            <p className="text-slate-400 mt-2">Manage employee training campaigns and track compliance</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAddEmployee(true)}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              data-testid="add-employee-btn"
            >
              <Users className="w-4 h-4" />
              Add Employee
            </button>
            <button
              onClick={() => setShowCreateCampaign(true)}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
              data-testid="create-campaign-btn"
            >
              <Plus className="w-4 h-4" />
              New Campaign
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Active Campaigns</p>
                  <p className="text-2xl font-bold text-white">{stats.activeCampaigns}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Total Employees</p>
                  <p className="text-2xl font-bold text-white">{stats.totalEmployees}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Compliance Rate</p>
                  <p className="text-2xl font-bold text-white">{stats.complianceRate}%</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">High Risk</p>
                  <p className="text-2xl font-bold text-white">{stats.highRiskEmployees}</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Avg Phish Click</p>
                  <p className="text-2xl font-bold text-white">{stats.avgPhishingClickRate}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2">
          {['overview', 'modules', 'campaigns', 'employees', 'phishing'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-t-lg font-medium capitalize transition-colors ${
                activeTab === tab
                  ? 'bg-emerald-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Risk Distribution Chart */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-emerald-400" />
                Employee Risk Distribution
              </h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsPie>
                    <Pie
                      data={riskDistributionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {riskDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={RISK_COLORS[index]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-slate-400 text-sm">Low Risk</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-slate-400 text-sm">Medium Risk</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-slate-400 text-sm">High Risk</span>
                </div>
              </div>
            </div>

            {/* Recent Campaigns */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-400" />
                Recent Campaigns
              </h3>
              {campaigns.length === 0 ? (
                <div className="text-center py-8">
                  <GraduationCap className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-400">No campaigns yet</p>
                  <button
                    onClick={() => setShowCreateCampaign(true)}
                    className="mt-3 text-emerald-400 hover:text-emerald-300"
                  >
                    Create your first campaign
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {campaigns.slice(0, 5).map((campaign) => (
                    <div key={campaign.id} className="p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-white font-medium">{campaign.name}</h4>
                          <p className="text-slate-400 text-sm">{campaign.type} • {campaign.totalAssigned} assigned</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs text-white ${STATUS_COLORS[campaign.status]}`}>
                          {campaign.status}
                        </span>
                      </div>
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-slate-400 mb-1">
                          <span>Completion</span>
                          <span>{campaign.completionRate}%</span>
                        </div>
                        <div className="h-2 bg-slate-600 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-emerald-500 rounded-full transition-all"
                            style={{ width: `${campaign.completionRate}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* High Risk Employees */}
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 lg:col-span-2">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                High Risk Employees
              </h3>
              {employees.filter(e => e.riskScore >= 70).length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
                  <p className="text-slate-400">No high-risk employees</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {employees.filter(e => e.riskScore >= 70).slice(0, 6).map((emp) => (
                    <div key={emp.id} className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                          <span className="text-red-400 font-bold">{emp.riskScore}</span>
                        </div>
                        <div>
                          <h4 className="text-white font-medium">{emp.name || emp.email}</h4>
                          <p className="text-slate-400 text-sm">{emp.department || 'No department'}</p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
                        <span>Failed Phishing: {emp.failedPhishingTests}</span>
                        <span>Completed: {emp.completedCampaigns}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modules Tab - Training Content Library */}
        {activeTab === 'modules' && (
          <div className="space-y-6">
            {/* My Progress Summary */}
            {myProgress && (
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  My Learning Progress
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-white">{myProgress.completedModules}</p>
                    <p className="text-slate-400 text-sm">Completed</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-white">{myProgress.inProgressModules}</p>
                    <p className="text-slate-400 text-sm">In Progress</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-emerald-400">{myProgress.overallProgress}%</p>
                    <p className="text-slate-400 text-sm">Overall Progress</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-blue-400">{myProgress.averageScore}%</p>
                    <p className="text-slate-400 text-sm">Avg Quiz Score</p>
                  </div>
                </div>
              </div>
            )}

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  !selectedCategory ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                All Modules
              </button>
              {Object.entries(moduleCategories).map(([key, cat]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === key ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Modules Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {modules
                .filter(m => !selectedCategory || m.category === selectedCategory)
                .map((module) => {
                  const progress = myProgress?.progress?.find(p => p.moduleId === module.id);
                  const categoryInfo = moduleCategories[module.category] || {};
                  
                  return (
                    <div 
                      key={module.id}
                      className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden hover:border-emerald-500/50 transition-colors cursor-pointer"
                      onClick={async () => {
                        setModuleLoading(true);
                        try {
                          const res = await trainingAPI.getModule(module.id);
                          setSelectedModule(res.data);
                          setShowModuleDetail(true);
                        } catch (error) {
                          console.error('Error loading module:', error);
                        } finally {
                          setModuleLoading(false);
                        }
                      }}
                    >
                      <div className={`h-2 ${
                        progress?.status === 'completed' ? 'bg-green-500' :
                        progress?.status === 'in_progress' ? 'bg-yellow-500' : 'bg-slate-600'
                      }`} />
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-slate-700`}>
                            <BookOpen className={`w-6 h-6 ${categoryInfo.color || 'text-emerald-400'}`} />
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            module.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' :
                            module.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {module.difficulty}
                          </span>
                        </div>
                        <h3 className="text-white font-semibold text-lg mb-2">{module.title}</h3>
                        <p className="text-slate-400 text-sm mb-4 line-clamp-2">{module.description}</p>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-4 text-slate-400">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {module.duration} min
                            </span>
                            <span className="flex items-center gap-1">
                              <Target className="w-4 h-4" />
                              {module.quizQuestions} questions
                            </span>
                          </div>
                          {progress?.status === 'completed' && (
                            <span className="flex items-center gap-1 text-green-400">
                              <CheckCircle className="w-4 h-4" />
                              {progress.quizScore}%
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Module Detail Modal */}
        {showModuleDetail && selectedModule && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-slate-700">
              <div className="p-6 border-b border-slate-700 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedModule.title}</h2>
                  <p className="text-slate-400 text-sm mt-1">{selectedModule.description}</p>
                </div>
                <button
                  onClick={() => {
                    setShowModuleDetail(false);
                    setSelectedModule(null);
                  }}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="flex items-center gap-4 mb-6 text-sm text-slate-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {selectedModule.duration} minutes
                  </span>
                  <span className={`px-2 py-1 rounded ${
                    selectedModule.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' :
                    selectedModule.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {selectedModule.difficulty}
                  </span>
                  <span>{selectedModule.quiz?.length || 0} quiz questions</span>
                </div>
                
                {/* Module Content */}
                {selectedModule.content?.map((section, index) => (
                  <div key={index} className="mb-6">
                    <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 bg-emerald-500/20 rounded flex items-center justify-center text-emerald-400 text-sm">
                        {index + 1}
                      </span>
                      {section.title}
                    </h3>
                    {section.type === 'text' && (
                      <div className="prose prose-invert prose-slate max-w-none">
                        <div className="text-slate-300 whitespace-pre-wrap bg-slate-900/50 rounded-lg p-4">
                          {section.content}
                        </div>
                      </div>
                    )}
                    {section.type === 'video' && (
                      <div className="bg-slate-900/50 rounded-lg p-4">
                        <p className="text-slate-300">{section.content}</p>
                        <p className="text-slate-500 text-sm mt-2">Duration: {section.duration} minutes</p>
                      </div>
                    )}
                    {section.type === 'interactive' && (
                      <div className="bg-slate-900/50 rounded-lg p-4">
                        <p className="text-slate-300 mb-4">{section.content}</p>
                        {section.questions?.map((q, qIdx) => (
                          <div key={qIdx} className="mb-3 p-3 bg-slate-800 rounded-lg">
                            <p className="text-white mb-2">{q.scenario}</p>
                            <p className={`text-sm ${q.isPhishing ? 'text-red-400' : 'text-green-400'}`}>
                              {q.isPhishing ? '⚠️ Phishing' : '✓ Legitimate'}
                            </p>
                            <p className="text-slate-400 text-sm mt-1">{q.explanation}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    {section.type === 'scenario' && (
                      <div className="bg-slate-900/50 rounded-lg p-4">
                        <div className="text-slate-300 whitespace-pre-wrap">{section.content}</div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Quiz Preview */}
                {selectedModule.quiz && selectedModule.quiz.length > 0 && (
                  <div className="mt-8 border-t border-slate-700 pt-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Target className="w-5 h-5 text-emerald-400" />
                      Quiz ({selectedModule.quiz.length} questions)
                    </h3>
                    <p className="text-slate-400 mb-4">Test your knowledge after completing the training content. You need 80% to pass.</p>
                    <div className="space-y-4">
                      {selectedModule.quiz.slice(0, 2).map((q, idx) => (
                        <div key={idx} className="bg-slate-900/50 rounded-lg p-4">
                          <p className="text-white mb-3">Q{idx + 1}: {q.question}</p>
                          <div className="space-y-2">
                            {q.options.map((opt, optIdx) => (
                              <div key={optIdx} className={`px-3 py-2 rounded ${
                                optIdx === q.correctAnswer ? 'bg-green-500/20 border border-green-500/30' : 'bg-slate-800'
                              }`}>
                                <span className="text-slate-300">{opt}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {selectedModule.quiz.length > 2 && (
                        <p className="text-slate-500 text-sm text-center">
                          + {selectedModule.quiz.length - 2} more questions
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowModuleDetail(false);
                    setSelectedModule(null);
                  }}
                  className="px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
                >
                  Close
                </button>
                <button
                  onClick={async () => {
                    try {
                      await trainingAPI.startModule(selectedModule.id);
                      loadData();
                    } catch (error) {
                      console.error('Error starting module:', error);
                    }
                  }}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Start Training
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="space-y-4">
            {campaigns.length === 0 ? (
              <div className="bg-slate-800 rounded-xl p-12 text-center border border-slate-700">
                <GraduationCap className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">No Training Campaigns</h3>
                <p className="text-slate-400 mb-4">Create your first training campaign to get started</p>
                <button
                  onClick={() => setShowCreateCampaign(true)}
                  className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                >
                  Create Campaign
                </button>
              </div>
            ) : (
              campaigns.map((campaign) => (
                <div key={campaign.id} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                        campaign.type === 'phishing' ? 'bg-orange-500/20' :
                        campaign.type === 'compliance' ? 'bg-blue-500/20' : 'bg-emerald-500/20'
                      }`}>
                        {campaign.type === 'phishing' ? (
                          <Mail className={`w-6 h-6 ${campaign.type === 'phishing' ? 'text-orange-400' : 'text-emerald-400'}`} />
                        ) : (
                          <BookOpen className="w-6 h-6 text-emerald-400" />
                        )}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold text-lg">{campaign.name}</h3>
                        <p className="text-slate-400 text-sm mt-1">{campaign.description || 'No description'}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className={`px-2 py-0.5 rounded ${STATUS_COLORS[campaign.status]} text-white`}>
                            {campaign.status}
                          </span>
                          <span className="text-slate-400">Type: {campaign.type}</span>
                          {campaign.dueDate && (
                            <span className="text-slate-400">Due: {new Date(campaign.dueDate).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {campaign.status === 'draft' && (
                        <button
                          onClick={() => handleActivateCampaign(campaign.id)}
                          className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                          title="Activate Campaign"
                        >
                          <Play className="w-5 h-5" />
                        </button>
                      )}
                      <button
                        onClick={() => openAssignModal(campaign)}
                        className="p-2 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                        title="Assign Employees"
                      >
                        <UserCheck className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteCampaign(campaign.id)}
                        className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Delete Campaign"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Progress Section */}
                  <div className="mt-6 grid grid-cols-3 gap-4">
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-white">{campaign.totalAssigned}</p>
                      <p className="text-slate-400 text-sm">Assigned</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-400">{campaign.totalCompleted}</p>
                      <p className="text-slate-400 text-sm">Completed</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{campaign.completionRate}%</p>
                      <p className="text-slate-400 text-sm">Completion Rate</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Employee Directory</h3>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
            </div>
            {filteredEmployees.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400">No employees found</p>
                <button
                  onClick={() => setShowAddEmployee(true)}
                  className="mt-3 text-emerald-400 hover:text-emerald-300"
                >
                  Add your first employee
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-700/50">
                      <th className="text-left p-4 text-slate-400 font-medium">Employee</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Department</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Risk Score</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Completed</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Failed Phishing</th>
                      <th className="text-left p-4 text-slate-400 font-medium">Last Training</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((emp) => {
                      const risk = getRiskLabel(emp.riskScore);
                      return (
                        <tr key={emp.id} className="border-t border-slate-700 hover:bg-slate-700/30">
                          <td className="p-4">
                            <div>
                              <p className="text-white font-medium">{emp.name || 'Unknown'}</p>
                              <p className="text-slate-400 text-sm">{emp.email}</p>
                            </div>
                          </td>
                          <td className="p-4 text-slate-300">{emp.department || '-'}</td>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className={`font-bold ${risk.color}`}>{emp.riskScore}</span>
                              <span className={`text-xs ${risk.color}`}>{risk.label}</span>
                            </div>
                          </td>
                          <td className="p-4 text-slate-300">{emp.completedCampaigns}</td>
                          <td className="p-4">
                            <span className={emp.failedPhishingTests > 0 ? 'text-red-400' : 'text-slate-300'}>
                              {emp.failedPhishingTests}
                            </span>
                          </td>
                          <td className="p-4 text-slate-400 text-sm">
                            {emp.lastTrainingDate ? new Date(emp.lastTrainingDate).toLocaleDateString() : 'Never'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Phishing Tab */}
        {activeTab === 'phishing' && (
          <div className="space-y-4">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Mail className="w-5 h-5 text-orange-400" />
                  Phishing Simulations
                </h3>
              </div>
              {phishingSimulations.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="w-12 h-12 mx-auto text-slate-600 mb-3" />
                  <p className="text-slate-400">No phishing simulations yet</p>
                  <p className="text-slate-500 text-sm mt-1">Phishing simulations help test employee awareness</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {phishingSimulations.map((sim) => (
                    <div key={sim.id} className="p-4 bg-slate-700/50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-white font-medium">{sim.name}</h4>
                          <p className="text-slate-400 text-sm">Template: {sim.templateType}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs ${STATUS_COLORS[sim.status]} text-white`}>
                          {sim.status}
                        </span>
                      </div>
                      {sim.status !== 'draft' && (
                        <div className="mt-4 grid grid-cols-4 gap-4">
                          <div className="text-center">
                            <p className="text-xl font-bold text-white">{sim.totalSent}</p>
                            <p className="text-slate-400 text-xs">Sent</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold text-blue-400">{sim.openRate}%</p>
                            <p className="text-slate-400 text-xs">Open Rate</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold text-red-400">{sim.clickRate}%</p>
                            <p className="text-slate-400 text-xs">Click Rate</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xl font-bold text-green-400">{sim.reportRate}%</p>
                            <p className="text-slate-400 text-xs">Report Rate</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create Campaign Modal */}
      {showCreateCampaign && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-lg border border-slate-700">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Create Training Campaign</h3>
              <button onClick={() => setShowCreateCampaign(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreateCampaign} className="p-6 space-y-4">
              <div>
                <label className="block text-slate-300 text-sm mb-1">Campaign Name</label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">Type</label>
                <select
                  value={campaignForm.type}
                  onChange={(e) => setCampaignForm({...campaignForm, type: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {CAMPAIGN_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">Description</label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({...campaignForm, description: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Due Date</label>
                  <input
                    type="date"
                    value={campaignForm.dueDate}
                    onChange={(e) => setCampaignForm({...campaignForm, dueDate: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-slate-300 text-sm mb-1">Passing Score (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={campaignForm.passingScore}
                    onChange={(e) => setCampaignForm({...campaignForm, passingScore: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateCampaign(false)}
                  className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                >
                  Create Campaign
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddEmployee && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-md border border-slate-700">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-white">Add Employee</h3>
              <button onClick={() => setShowAddEmployee(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddEmployee} className="p-6 space-y-4">
              <div>
                <label className="block text-slate-300 text-sm mb-1">Email</label>
                <input
                  type="email"
                  value={employeeForm.email}
                  onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">Name</label>
                <input
                  type="text"
                  value={employeeForm.name}
                  onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">Department</label>
                <input
                  type="text"
                  value={employeeForm.department}
                  onChange={(e) => setEmployeeForm({...employeeForm, department: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-slate-300 text-sm mb-1">Job Title</label>
                <input
                  type="text"
                  value={employeeForm.jobTitle}
                  onChange={(e) => setEmployeeForm({...employeeForm, jobTitle: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddEmployee(false)}
                  className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                >
                  Add Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Employees Modal */}
      {showAssignModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl w-full max-w-lg border border-slate-700 max-h-[80vh] flex flex-col">
            <div className="p-6 border-b border-slate-700 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-xl font-semibold text-white">Assign Employees</h3>
                <p className="text-slate-400 text-sm">{selectedCampaign.name}</p>
              </div>
              <button onClick={() => setShowAssignModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 border-b border-slate-700 flex-shrink-0">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {filteredEmployees.length === 0 ? (
                <p className="text-slate-400 text-center py-4">No employees found</p>
              ) : (
                <div className="space-y-2">
                  {filteredEmployees.map((emp) => {
                    const isSelected = selectedEmployees.some(e => e.id === emp.id);
                    return (
                      <div
                        key={emp.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedEmployees(selectedEmployees.filter(e => e.id !== emp.id));
                          } else {
                            setSelectedEmployees([...selectedEmployees, emp]);
                          }
                        }}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-emerald-500/20 border border-emerald-500/50' : 'bg-slate-700/50 hover:bg-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected ? 'bg-emerald-500 border-emerald-500' : 'border-slate-500'
                          }`}>
                            {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                          <div>
                            <p className="text-white">{emp.name || emp.email}</p>
                            <p className="text-slate-400 text-sm">{emp.email}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-700 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-slate-400">{selectedEmployees.length} selected</span>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignEmployees}
                  disabled={selectedEmployees.length === 0}
                  className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-600 text-white rounded-lg"
                >
                  Assign Selected
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingDashboard;
